import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Settings, Users, Mail, AlertTriangle, UserCog, ClipboardList } from 'lucide-react';
import { DivisionPointsTab, ManageScorecards, FixNamesTab, FullMatchTab } from '../components/admin';

export default function AdminSettings({ user }) {
  const [configs, setConfigs] = useState([]);
  const [scorecards, setScorecards] = useState([]);
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('divisions');

  const fetchConfigs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/division-configs`);
      setConfigs(response.data);
    } catch (error) {
      toast.error('Failed to load division configurations');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRegisteredUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setRegisteredUsers(response.data);
    } catch (error) {
      // Silent fail
    }
  }, []);

  const fetchAllScorecards = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/scorecards`);
      setScorecards(response.data);
    } catch (error) {
      toast.error('Failed to load scorecards');
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
    fetchAllScorecards();
    fetchRegisteredUsers();
  }, [fetchConfigs, fetchAllScorecards, fetchRegisteredUsers]);

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-stone-600 font-inter">Loading settings...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Settings className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
            <h1 className="font-barlow font-bold text-4xl text-slate-900 uppercase tracking-tight">
              Admin Settings
            </h1>
          </div>
          <p className="text-stone-600 font-inter">
            Configure division point rules, manage scorecards, and fix data issues
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-stone-200 flex-wrap">
          <button
            onClick={() => setActiveTab('divisions')}
            className={`font-barlow font-semibold uppercase tracking-wide text-sm px-6 py-3 ${
              activeTab === 'divisions'
                ? 'border-b-2 border-amber-600 text-amber-600'
                : 'text-stone-600 hover:text-slate-900'
            }`}
          >
            Division Points
          </button>
          <button
            onClick={() => setActiveTab('scorecards')}
            className={`font-barlow font-semibold uppercase tracking-wide text-sm px-6 py-3 ${
              activeTab === 'scorecards'
                ? 'border-b-2 border-amber-600 text-amber-600'
                : 'text-stone-600 hover:text-slate-900'
            }`}
          >
            Manage Scorecards
          </button>
          <button
            onClick={() => setActiveTab('fix-names')}
            className={`font-barlow font-semibold uppercase tracking-wide text-sm px-6 py-3 ${
              activeTab === 'fix-names'
                ? 'border-b-2 border-amber-600 text-amber-600'
                : 'text-stone-600 hover:text-slate-900'
            }`}
          >
            <UserCog className="w-4 h-4 inline mr-1" />
            Fix Names
          </button>
          <button
            onClick={() => setActiveTab('full-match')}
            className={`font-barlow font-semibold uppercase tracking-wide text-sm px-6 py-3 ${
              activeTab === 'full-match'
                ? 'border-b-2 border-amber-600 text-amber-600'
                : 'text-stone-600 hover:text-slate-900'
            }`}
          >
            <ClipboardList className="w-4 h-4 inline mr-1" />
            Full Match
          </button>
          <a
            href="/admin/users"
            className="font-barlow font-semibold uppercase tracking-wide text-sm px-6 py-3 text-stone-600 hover:text-slate-900"
          >
            <Users className="w-4 h-4 inline mr-1" />
            Users →
          </a>
          <a
            href="/admin/emails"
            className="font-barlow font-semibold uppercase tracking-wide text-sm px-6 py-3 text-stone-600 hover:text-slate-900"
          >
            <Mail className="w-4 h-4 inline mr-1" />
            Email Uploads →
          </a>
          <a
            href="/admin/duplicates"
            className="font-barlow font-semibold uppercase tracking-wide text-sm px-6 py-3 text-stone-600 hover:text-slate-900"
          >
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            Duplicate Reviews →
          </a>
        </div>

        {/* Tab Content */}
        {activeTab === 'divisions' && (
          <DivisionPointsTab 
            configs={configs} 
            setConfigs={setConfigs} 
            onRefresh={fetchConfigs} 
          />
        )}

        {activeTab === 'scorecards' && (
          <ManageScorecards 
            scorecards={scorecards} 
            onRefresh={fetchAllScorecards} 
          />
        )}

        {activeTab === 'fix-names' && (
          <FixNamesTab />
        )}

        {activeTab === 'full-match' && (
          <FullMatchTab 
            configs={configs}
            registeredUsers={registeredUsers}
            onScorecardCreated={fetchAllScorecards}
          />
        )}
      </div>
    </Layout>
  );
}
